import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as XLSX from 'xlsx';
import { askOurDocsRoles } from "@shared/constants/roles/roles";
import { featureFlags } from '@app/environments/environment';
import { AskOurDocsV2Service } from '@app/core/services/ask-our-docs-v2/ask-our-docs-v2.service';
import { AlertService } from '@app/core/services/alert/alert.service';

@Component({
  selector: 'app-admin-landing-feedback-form',
  templateUrl: './admin-landing-feedback-form.component.html',
  styleUrls: ['./admin-landing-feedback-form.component.css']
})
export class AdminLandingFeedbackFormComponent implements OnInit {
  @Input() indexName: string = '';
  @Output() redirectionToCustomizableFeedback = new EventEmitter<boolean>;
  feedbackReportsForm!: FormGroup;
  cutoffDate!: string;
  startDate!: string;
  endDate!: string;
  protected readonly featureFlags = featureFlags;

  constructor(private router: Router, private route: ActivatedRoute, private fb: FormBuilder, private askOurDocsV2Service : AskOurDocsV2Service, private alertService: AlertService) {}

  ngOnInit(): void {
    this.cutoffDate = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0];

    this.feedbackReportsForm = this.fb.group({
      start_date: ['', Validators.required],
      end_date: ['', Validators.required]
    }, { validator: this.dateRangeValidator });

    this.feedbackReportsForm.valueChanges.subscribe((group) => {
      this.dateRangeValidator(group);
    });
  }

  redirectToFeedback() {
    this.redirectionToCustomizableFeedback.emit(true);
  }

  dateRangeValidator(group: FormGroup): { [key: string]: any } | null {
    const startControl = group.get('start_date');
    const endControl = group.get('end_date');

    if (!startControl || !endControl) {
      return null;
    }

    const start = startControl.value;
    const end = endControl.value;
    const startDateLimit = '2024-03-01';
    const cutoffDate = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0];
    const isStartDateValid = start >= startDateLimit;
    const isEndDateValid = end <= cutoffDate;

    if (!start || !end) {
      return null;
    }

    const isDateRangeValid = start <= end;

    return isStartDateValid && isEndDateValid && isDateRangeValid ? null : { dateRangeInvalid: true };
  }

  downloadExcel(): void {
    const startControl = this.feedbackReportsForm.get('start_date');
    const endControl = this.feedbackReportsForm.get('end_date');
    this.startDate = startControl?.value;
    this.endDate = endControl?.value;
    this.feedbackReportsForm.reset();

    const payload = {
      indexName: this.indexName,
      start_date: this.startDate,
      end_date: this.endDate
    };

    this.askOurDocsV2Service.getFeedbackReport(payload).subscribe((data : any[]) => {
      if(data.length === 0) {
        this.alertService.showError("No data available");
      } else {
        const headers = this.generateHeaders(data);
        this.generateExcel(headers, data);
      }
    },
    (error)=>{
      console.log('Failed to fetch report', error)
    }
  );
  }

  private generateHeaders( data : any[]): string[] {
    const headers = ['Index Name', 'Answer Helpful', 'Datetime Stamp', 'Conversation'];
    const feedbackKeys = new Set<string>();

    data.forEach(item => {
      item.feedback.forEach((feedback: any) => {
        Object.keys(feedback).forEach(key => {
          feedbackKeys.add(`${key.replace('f', 'F')}(Type/QueryText/Response/otherOptionResponse)`);
        });
      });
    });

    return headers.concat(Array.from(feedbackKeys));
  }

  private generateExcel(headers: string[],  data : any[]): void {
    const worksheetData = [headers];
    data.forEach(item => {
      const row = [
        item.indexName,
        item.answerHelpful,
        item.timeStamp,
        item.conversation
      ];
      item.feedback.forEach((feedback: any) => {
        const feedbackValues = Object.values(feedback)[0];
        row.push(feedbackValues);
      });
      worksheetData.push(row);
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, `Feedback_${this.indexName.replace(askOurDocsRoles.IndexPrefix, "").replaceAll("_", " ")}_${this.startDate}_${this.endDate}`);
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const blob: Blob = new Blob([buffer], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
